import React, { useMemo } from 'react';
import { Check, ChevronsUpDown, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import { trpc } from '@/lib/trpc';

interface OrgOption {
  id: string;
  name: string;
  slug?: string | null;
  role?: string | null;
  thumbnailUrl?: string | null;
}

function OrgThumbnail({ src, name, size = 'sm' }: { src?: string | null; name: string; size?: 'sm' | 'md' }) {
  const dim = size === 'md' ? 'h-5 w-5' : 'h-4 w-4';
  if (!src) {
    return <Store className={`${dim} shrink-0 text-muted-foreground`} />;
  }
  return (
    <img
      src={src}
      alt={name}
      className={`${dim} shrink-0 rounded-sm object-cover`}
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = 'none';
      }}
    />
  );
}

export default function OrgSwitcher() {
  const { organization } = useOrganization();
  const { profile, loading: authLoading } = useAuth();
  const [isOpen, setIsOpen] = React.useState(false);

  const {
    data: orgOptions = [],
    isLoading: orgLoading,
    isFetched: orgOptionsFetched,
  } = trpc.organization.getOrgLocationOptions.useQuery(
    undefined,
    {
      enabled: !!profile?.id && !authLoading,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    }
  );

  const isLoading = authLoading;

  const orgs = useMemo<OrgOption[]>(() => {
    if (authLoading) return [];
    const options = orgOptions
      .filter((org) => typeof org.id === 'string')
      .map((org) => ({
        id: org.id,
        name: org.name,
        slug: org.slug,
        role: org.role,
        thumbnailUrl: org.thumbnailUrl,
      }));

    // Fallback for first paint before options are fetched/opened.
    if (options.length === 0 && organization?.id) {
      return [
        {
          id: organization.id,
          name: organization.name,
          slug: organization.slug,
          thumbnailUrl: organization.thumbnail_url,
        },
      ];
    }

    return options;
  }, [orgOptions, authLoading, organization?.id, organization?.name, organization?.slug, organization?.thumbnail_url]);

  const currentOrg = useMemo(() => {
    if (organization?.id) {
      return (
        orgs.find((o) => o.id === organization.id) ?? {
          id: organization.id,
          name: organization.name,
          slug: organization.slug,
          thumbnailUrl: organization.thumbnail_url,
        }
      );
    }
    return orgs[0];
  }, [orgs, organization?.id, organization?.name, organization?.slug, organization?.thumbnail_url]);

  const handleSelect = (orgId: string) => {
    if (orgId === organization?.id) {
      setIsOpen(false);
      return;
    }
    localStorage.setItem('activeOrgId', orgId);
    window.dispatchEvent(new CustomEvent('org-changed', { detail: { organizationId: orgId } }));
    setIsOpen(false);
  };

  if (isLoading) {
    return <Skeleton className="h-9 w-56" />;
  }

  // Single org - show static label once options are loaded.
  if (orgOptionsFetched && !orgLoading && orgs.length <= 1) {
    return (
      <div className="flex items-center gap-2 px-2">
        <OrgThumbnail src={currentOrg?.thumbnailUrl} name={currentOrg?.name ?? ''} size="md" />
        <span className="text-sm font-medium">{currentOrg?.name ?? 'Store'}</span>
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          className="w-56 justify-between"
        >
          <span className="flex items-center gap-2 truncate">
            <OrgThumbnail src={currentOrg?.thumbnailUrl} name={currentOrg?.name ?? ''} />
            <span className="text-sm font-medium truncate">{currentOrg?.name ?? 'Select store'}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search stores..." />
          <CommandList>
            <CommandEmpty>No stores found.</CommandEmpty>
            <CommandGroup>
              {orgs.map((org) => (
                <CommandItem
                  key={org.id}
                  onSelect={() => handleSelect(org.id)}
                  className="flex items-center justify-between"
                >
                  <span className="flex items-center gap-2 font-medium">
                    <OrgThumbnail src={org.thumbnailUrl} name={org.name} />
                    {org.name}
                  </span>
                  {org.id === organization?.id && <Check className="h-4 w-4" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
